/**
 * SIMO-UI-MOBILE-RESPONSIVE-AND-ESCAPE-PASS-1 — Modal-Komponente.
 *
 * Loest F-8 (7 Modals ohne Esc-Hook) zentral: ein einziger Esc-Handler
 * mit sauberer Listener-Cleanup-Disziplin. Wird in CodexView/SettingsView
 * fuer alle Dialog-Overlays genutzt.
 *
 * Verhalten:
 *   - `Esc` schliesst den Dialog (kann via `dismissOnEscape={false}` deaktiviert
 *     werden, z.B. waehrend einer laufenden destruktiven Operation).
 *   - Klick auf den Backdrop schliesst den Dialog (`dismissOnBackdrop={false}`
 *     deaktiviert das ebenfalls).
 *   - Focus springt beim Oeffnen auf den ersten fokussierbaren Inhalt.
 *     Beim Schliessen wird der vorherige Focus wiederhergestellt (a11y).
 *   - Tab und Shift+Tab bleiben innerhalb des Dialogs.
 *   - Body-Scroll wird waehrend offenem Dialog gesperrt und danach exakt
 *     wiederhergestellt.
 *   - `role="dialog"` + `aria-modal="true"` + `aria-labelledby` wenn `titleId`
 *     gesetzt.
 *   - Effekt-Cleanup haengt am `open`-Flag: keine dangling Listener nach
 *     Unmount oder Close.
 */

import { useEffect, useRef } from "react";
import type { ReactNode, KeyboardEvent as ReactKeyboardEvent } from "react";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Tailwind-max-width-Klasse fuer das Card-Container. */
  maxWidth?: string;
  /** Esc-Taste schliesst. Default `true`. */
  dismissOnEscape?: boolean;
  /** Backdrop-Klick schliesst. Default `true`. */
  dismissOnBackdrop?: boolean;
  /** Optional: id des Title-Elements fuer `aria-labelledby`. */
  titleId?: string;
  /** Optional: id der Beschreibung fuer `aria-describedby`. */
  descriptionId?: string;
  /** Optional: zusaetzliche Backdrop-Klassen, z. B. fuer einen Drawer. */
  backdropClassName?: string;
  /** Optional: zusaetzliche Surface-Klassen. */
  surfaceClassName?: string;
}

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

function getFocusableElements(container: HTMLElement | null): HTMLElement[] {
  if (!container) return [];
  return Array.from(container.querySelectorAll<HTMLElement>(focusableSelector)).filter((element) => {
    if (element.hasAttribute("disabled")) return false;
    if (element.getAttribute("aria-hidden") === "true") return false;
    return true;
  });
}

export function Modal({
  open,
  onClose,
  children,
  maxWidth = "max-w-md",
  dismissOnEscape = true,
  dismissOnBackdrop = true,
  titleId,
  descriptionId,
  backdropClassName = "",
  surfaceClassName = "",
}: ModalProps): JSX.Element | null {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const previousBodyOverflowRef = useRef<string>("");
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Esc-Handler + Focus-Restore. Effekt-Cleanup haengt am `open`-Flag.
  useEffect(() => {
    if (!open) return;

    previousFocusRef.current = (document.activeElement as HTMLElement | null) ?? null;
    previousBodyOverflowRef.current = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Den ersten fokussierbaren Inhalt im Card-Container suchen und Focus
    // setzen. Faellt auf den Container selbst zurueck, wenn nichts gefunden.
    const focusables = getFocusableElements(cardRef.current);
    const target = focusables[0] ?? cardRef.current;
    target?.focus();

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && dismissOnEscape) {
        e.preventDefault();
        onCloseRef.current();
        return;
      }

      if (e.key === "Tab") {
        const card = cardRef.current;
        const focusableElements = getFocusableElements(card);
        if (!card || focusableElements.length === 0) {
          e.preventDefault();
          card?.focus();
          return;
        }

        const first = focusableElements[0]!;
        const last = focusableElements[focusableElements.length - 1]!;
        const active = document.activeElement;

        if (!card.contains(active)) {
          e.preventDefault();
          first.focus();
          return;
        }

        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
          return;
        }

        if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      previousFocusRef.current?.focus?.();
      document.body.style.overflow = previousBodyOverflowRef.current;
    };
  }, [open, dismissOnEscape]);

  if (!open) return null;

  const onBackdropClick = (): void => {
    if (dismissOnBackdrop) onClose();
  };

  // Klick im Card-Bereich darf den Backdrop-Close nicht triggern.
  const onCardClick = (e: ReactKeyboardEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>): void => {
    e.stopPropagation();
  };

  return (
    <div
      className={`fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 ${backdropClassName}`.trim()}
      onClick={onBackdropClick}
      role="presentation"
    >
      <div
        ref={cardRef}
        className={`card simo-keyboard-safe-editor simo-modal-card p-6 w-full ${maxWidth} overflow-y-auto ${surfaceClassName}`.trim()}
        onClick={onCardClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
      >
        {children}
      </div>
    </div>
  );
}
